import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import { T, M } from 'TOMA';
import s from './Crm.module.scss';
import routes from 'router/routes';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  iProspectFileForm,
  iCrmCompany,
  iCrmContact,
  eCrmProspectStatus,
  eCrmPriority,
} from 'services/Crm/Crm.type';

const STATUSES: eCrmProspectStatus[] = [
  'new',
  'contacted',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
];

const PRIORITIES: eCrmPriority[] = ['low', 'medium', 'high'];

function CrmProspectFileFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { prospectId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!prospectId;
  const { crmRepository, eventRepository } = useApiContext();
  const { t } = useTranslation();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [contacts, setContacts] = useState<iCrmContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const { response: events = [], loading: eventsLoading } = useRepo(
    async () => await eventRepository.getEvents(),
    []
  );

  const { response: companies = [], loading: companiesLoading } = useRepo(
    async () => await crmRepository.getCompanies(),
    []
  );

  const { response: existing, loading: existingLoading } = useRepo(
    async () =>
      isEdit ? await crmRepository.getProspectFile(Number(prospectId)) : null,
    [prospectId]
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<iProspectFileForm>();

  const watchedCompanyId = watch('company_id');

  useEffect(() => {
    const eventIdParam = searchParams.get('eventId');
    if (eventIdParam && !isEdit) {
      setValue('event_id', Number(eventIdParam));
    }
  }, [searchParams, isEdit, setValue]);

  useEffect(() => {
    if (existing) {
      reset({
        event_id: existing.event_id,
        company_id: existing.company_id,
        contact_id: existing.contact_id,
        status: existing.status,
        priority: existing.priority,
        source: existing.source ?? '',
        estimated_surface: existing.estimated_surface ?? undefined,
        estimated_budget: existing.estimated_budget ?? undefined,
        notes: existing.notes ?? '',
      });
      setSelectedCompanyId(existing.company_id);
    }
  }, [existing, reset]);

  useEffect(() => {
    if (watchedCompanyId && Number(watchedCompanyId) !== selectedCompanyId) {
      setSelectedCompanyId(Number(watchedCompanyId));
    }
  }, [watchedCompanyId]);

  useEffect(() => {
    if (selectedCompanyId) {
      setContactsLoading(true);
      crmRepository
        .getContactsByCompany(selectedCompanyId)
        .then((data) => setContacts(data))
        .catch(() => setContacts([]))
        .finally(() => setContactsLoading(false));
    } else {
      setContacts([]);
    }
  }, [selectedCompanyId]);

  const onSubmit = async (data: iProspectFileForm): Promise<void> => {
    try {
      const payload = {
        ...data,
        event_id: Number(data.event_id),
        company_id: Number(data.company_id),
        contact_id: data.contact_id ? Number(data.contact_id) : null,
        estimated_surface: data.estimated_surface
          ? Number(data.estimated_surface)
          : undefined,
        estimated_budget: data.estimated_budget
          ? Number(data.estimated_budget)
          : undefined,
      };
      if (isEdit) {
        await crmRepository.updateProspectFile(Number(prospectId), payload);
        toast.success(t('crm:prospect_updated'));
      } else {
        await crmRepository.createProspectFile(payload);
        toast.success(t('crm:prospect_created'));
      }
      navigate(routes.crm.path);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  if (isEdit && existingLoading) {
    return (
      <T.Container>
        <M.Loading loading={true} error={null} fullWiewport>
          <div />
        </M.Loading>
      </T.Container>
    );
  }

  return (
    <div>
      <T.Container>
        <div className={s.pageHeader}>
          <h1 className={s.pageTitle}>
            {isEdit ? t('crm:edit_prospect') : t('crm:new_prospect')}
          </h1>
        </div>

        <M.Loading
          loading={eventsLoading || companiesLoading}
          error={null}
          fullWiewport
        >
          <div className={s.formCard}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={s.formGrid}>
                <div className={s.formGroup}>
                  <label className={s.formLabel}>
                    {t('crm:select_event')}
                    <span className={s.required}> *</span>
                  </label>
                  <select
                    {...register('event_id', { required: true })}
                    className={`${s.formSelect} ${
                      errors.event_id ? s.inputError : ''
                    }`}
                  >
                    <option value="">--</option>
                    {(events || []).map((event: any) => (
                      <option key={event.id} value={event.id}>
                        {event.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={s.formGroup}>
                  <label className={s.formLabel}>
                    {t('crm:select_company')}
                    <span className={s.required}> *</span>
                  </label>
                  <select
                    {...register('company_id', { required: true })}
                    className={`${s.formSelect} ${
                      errors.company_id ? s.inputError : ''
                    }`}
                  >
                    <option value="">--</option>
                    {(companies || []).map((c: iCrmCompany) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={s.formGroup}>
                  <label className={s.formLabel}>
                    {t('crm:select_contact')}
                  </label>
                  <select
                    {...register('contact_id')}
                    className={s.formSelect}
                    disabled={contactsLoading || contacts.length === 0}
                  >
                    <option value="">--</option>
                    {contacts.map((c: iCrmContact) => (
                      <option key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={s.formGroup}>
                  <label className={s.formLabel}>{t('crm:source')}</label>
                  <input
                    type="text"
                    {...register('source')}
                    className={s.formInput}
                    placeholder="ex: Salon, Site web, LinkedIn..."
                  />
                </div>

                <div className={s.formGroup}>
                  <label className={s.formLabel}>{t('crm:status')}</label>
                  <select {...register('status')} className={s.formSelect}>
                    {STATUSES.map((st) => (
                      <option key={st} value={st}>
                        {t(`crm:status_${st}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={s.formGroup}>
                  <label className={s.formLabel}>{t('crm:priority')}</label>
                  <select {...register('priority')} className={s.formSelect}>
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {t(`crm:priority_${p}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={s.formGroup}>
                  <label className={s.formLabel}>
                    {t('crm:estimated_surface')}
                  </label>
                  <input
                    type="number"
                    {...register('estimated_surface')}
                    className={s.formInput}
                    placeholder="m\u00B2"
                  />
                </div>

                <div className={s.formGroup}>
                  <label className={s.formLabel}>
                    {t('crm:estimated_budget')}
                  </label>
                  <input
                    type="number"
                    {...register('estimated_budget')}
                    className={s.formInput}
                    placeholder="\u20AC"
                  />
                </div>

                <div className={s.formGroupFull}>
                  <label className={s.formLabel}>{t('crm:notes')}</label>
                  <textarea
                    {...register('notes')}
                    rows={4}
                    className={s.formTextarea}
                  />
                </div>
              </div>

              <div className={s.formActions}>
                <button
                  type="submit"
                  className={s.btnPrimary}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '...' : t('crm:save')}
                </button>
                <button
                  type="button"
                  className={s.btnLink}
                  onClick={() => navigate(routes.crm.path)}
                >
                  {t('crm:cancel')}
                </button>
              </div>
            </form>
          </div>
        </M.Loading>
      </T.Container>
    </div>
  );
}

export default CrmProspectFileFormPage;
